import {VenueSubmissionFormState} from '../../../../types'
import {CountryCode} from 'libphonenumber-js'

export type FormFieldType = {
  label: string
  placeholder: string
  required: boolean
  id: string
  errorMessage?: string | null
  formKey: string
}

export const venueDetailsFormFields: FormFieldType[] = [
  {
    label: 'Venue Name',
    placeholder: 'Enter the name of the venue...',
    required: true,
    id: 'venue_submission_venueName',
    errorMessage: null,
    formKey: 'venueName',
  },
  {
    label: 'Address Line 1',
    placeholder: 'Enter the address of the venue...',
    required: true,
    id: 'venue_submission_addressLine1',
    errorMessage: null,
    formKey: 'addressLine1',
  },
  {
    label: 'Address Line 2',
    placeholder: 'Units, Suite, Floor, etc. (optional)',
    required: false,
    id: 'venue_submission_addressLine2',
    errorMessage: null,
    formKey: 'addressLine2',
  },
  {
    label: 'City',
    placeholder: 'Enter the city...',
    required: true,
    id: 'venue_submission_city',
    errorMessage: null,
    formKey: 'city',
  },
  {
    label: 'Postal Code',
    placeholder: 'Enter postal code...',
    required: true,
    id: 'venue_submission_postalCode',
    errorMessage: null,
    formKey: 'postalCode',
  },
  {
    label: 'Notes',
    placeholder: `Anything we should know about the venue or any notes you'd like to add...`,
    required: false,
    id: 'venue_submission_additionalNotes',
    errorMessage: null,
    formKey: 'additionalNotes',
  },
]

export const regionFormFields = {
  label: 'Country',
  placeholder: 'Enter country...',
  required: true,
  id: 'venue_submission_country',
  errorMessage: null,
  formKey: 'country',
  state: {
    label: 'State/Province',
    placeholder: 'Enter state or province...',
    required: true,
    id: 'venue_submission_stateOrProvince',
    errorMessage: null,
    formKey: 'stateOrProvince',
  },
}

export const contactDetailsFormFields: FormFieldType[] = [
  {
    label: 'Contact Name',
    placeholder: 'Enter contact name...',
    required: true,
    id: 'venue_submission_contactName',
    errorMessage: null,
    formKey: 'contactName',
  },
  {
    label: 'Contact Email',
    placeholder: 'Enter email...',
    required: false,
    id: 'venue_submission_contactEmail',
    errorMessage: null,
    formKey: 'contactEmail',
  },
]

export const getCollectionDayKey = (day: string) => {
  return `venueOpen${day}` as keyof VenueSubmissionFormState['collectionRequest']
}

export const getRegion = (countryCode: string) => {
  if (countryCode === 'AU') return 'AUS'
  if (countryCode === 'NZ') return 'NZ'
  return 'UKE'
}

// Mock API call function - for testing purposes only
export const mockAPICall = (collectionRequest: any) => {
  return new Promise((resolve, reject) => {
    setTimeout(() => {
      return Math.random() > 0.8
        ? resolve({success: true, data: {collectionRequest, id: Date.now()}})
        : reject(new Error('Error submitting.'))
    }, 500)
  })
}

// Retry logic with exponential backoff - for testing purposes only
export const retryOperation = async (func: any, max: any, delay: any, ...args: any) => {
  let attempts = 0
  while (attempts < max) {
    try {
      return await func(...args)
    } catch (error) {
      attempts++
      if (attempts === max) {
        throw error
      }
      console.log(`Attempt ${attempts} failed. Retrying in ${delay}`)
      // eslint-disable-next-line
      await new Promise((resolve) => setTimeout(resolve, delay))
      delay *= 2
    }
  }
}

type CountryOption = {
  value: string // 'US'
  label: string // 'United States'
}

export function filterCountryOptions(
  countryOptions: CountryOption[],
  phoneNumberCountries: string[]
): CountryOption[] {
  // Convert the phone number country list into a Set for fast lookups
  const allowedCountrySet = new Set(phoneNumberCountries)

  // Filter options to only those whose value is an allowed country code
  return countryOptions.filter((option) => allowedCountrySet.has(option.value))
}

export const cantFindLocationEmailAddress = (countryCode: CountryCode) => {
  if (countryCode === 'AU' || countryCode === 'NZ') return 'rent@kegstar.com'
  return 'collections@kegstar.com'
}

export const compactAddress = (address: VenueSubmissionFormState['address']) => {
  const parts = [
    address.addressLine1,
    address.addressLine2,
    address.city,
    address.stateOrProvince?.value,
    address.postalCode,
    address.country?.value,
  ].filter(Boolean)
  return parts.join(', ')
}

export const cantFindLocationEmailSubjectText = 'New Kegstar Venue and Keg Pickup Request'

export const cantFindLocationMailToBody = ({
  venueName,
  address,
  contactName,
  contactEmail,
  contactPhone,
  additionalNotes,
  includeKegCollection,
  collectionRequest,
}: {
  venueName: string
  address: VenueSubmissionFormState['address']
  contactName: string
  contactEmail: string
  contactPhone: string
  additionalNotes: string | undefined
  includeKegCollection: boolean
  collectionRequest: VenueSubmissionFormState['collectionRequest']
}) => {
  const baseLines = [
    'New Venue Submission Details:',
    '',
    `Venue Name: ${venueName}`,
    `Venue Address: ${compactAddress(address)}`,
    `Contact Name: ${contactName}`,
    `Contact Email: ${contactEmail}`,
    `Contact Phone: ${contactPhone}`,
    `Additional Notes: ${additionalNotes || 'N/A'}`,
    '',
  ]

  if (!includeKegCollection) {
    return [...baseLines, 'No keg collection request included.'].join('\n').trim()
  }

  const {
    '20L': qty20,
    '30L': qty30,
    '50L': qty50,
    '9G': qty9G,
    venueOpenMonday,
    venueOpenTuesday,
    venueOpenWednesday,
    venueOpenThursday,
    venueOpenFriday,
    startHour,
    endHour,
    kegLocation,
    pickupInstructions,
  } = collectionRequest

  const days: string[] = []
  if (venueOpenMonday) days.push('Monday')
  if (venueOpenTuesday) days.push('Tuesday')
  if (venueOpenWednesday) days.push('Wednesday')
  if (venueOpenThursday) days.push('Thursday')
  if (venueOpenFriday) days.push('Friday')

  const collectionLines = [
    'Keg Collection Request Details:',
    '',
    'Keg Sizes (Quantity):',
    `  20L: ${qty20 || '0'}`,
    `  30L: ${qty30 || '0'}`,
    `  50L: ${qty50 || '0'}`,
    `  9G: ${qty9G || '0'}`,
    '',
    `Available Collection Days: ${days.join(', ') || 'N/A'}`,
    `Available Collection Times: ${startHour}:00 - ${endHour}:00`,
    `Keg Locations: ${kegLocation}`,
    `Special notes for our drivers: ${pickupInstructions || 'N/A'}`,
  ]

  return [...baseLines, ...collectionLines].join('\n').trim()
}

export const getCountryData = (countryCode: string, options: {value: string; label: string}[]) => {
  const countryData = options.find((country) => country.value === countryCode)
  if (countryData) return countryData
  return {value: '', label: ''}
}
